import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError, channelSecrets, type Channel, type CreatedChannel } from '../api';
import { useChannels, useMe } from '../queries';
import { Icon, useNow, when } from '../ui';

/** live / waiting / disabled, as the list and the page show it. */
export function ChannelBadge({ c }: { c: Channel }) {
  const [cls, label] = c.status === 'disabled' ? ['cancelled', 'Disabled'] : c.live_stream_id ? ['live', 'Live'] : ['pending', 'Waiting'];
  return (
    <span className={`badge ${cls}`}>
      <span className="dot" />
      {label}
    </span>
  );
}

export function Channels() {
  const q = useChannels();
  const now = useNow(30_000);
  const [creating, setCreating] = useState(false);
  const rows = q.data?.data ?? [];
  return (
    <section className="page">
      <div className="page-head">
        <div>
          <h1>Channels</h1>
          <p className="sub">One address and key to save in the encoder once. Every time it connects, a new stream starts.</p>
        </div>
        {!creating && (
          <button type="button" className="btn primary" onClick={() => setCreating(true)}>
            <Icon name="plus" size={14} width={2} />
            New channel
          </button>
        )}
      </div>

      {creating && <NewChannel onCancel={() => setCreating(false)} />}

      {q.isPending ? (
        <p className="muted">Loading…</p>
      ) : q.error ? (
        <p className="error-text">{q.error.message}</p>
      ) : rows.length === 0 ? (
        !creating && (
          <div className="table">
            <div className="empty">
              <Icon name="channel" size={28} width={1.2} />
              <span>No channels yet. Use one for anything you broadcast again and again: a weekly class, a service, a show.</span>
              <button type="button" className="btn primary" onClick={() => setCreating(true)}>
                New channel
              </button>
            </div>
          </div>
        )
      ) : (
        <div className="table channels" role="table" aria-label="Channels">
          <div className="tr th" role="row">
            <span role="columnheader">Channel</span>
            <span role="columnheader">Status</span>
            <span role="columnheader">Ingest</span>
            <span role="columnheader">Next</span>
            <span role="columnheader">Created</span>
          </div>
          {rows.map((c) => (
            <Link key={c.id} to="/channels/$id" params={{ id: c.id }} className="tr" role="row">
              <span className="name" role="cell">
                  <b>{c.name ?? 'Untitled channel'}</b>
                  <span>{c.id}</span>
              </span>
              <span className="st" role="cell">
                <ChannelBadge c={c} />
              </span>
              <span className="cell-mono" role="cell">{c.rtmp_url.replace(/^rtmp:\/\//, '').split('/')[0]}</span>
              <span className="muted" role="cell">{c.scheduled_at ? new Date(c.scheduled_at).toLocaleString() : '—'}</span>
              <span className="muted" role="cell">{when(c.created_at, now)}</span>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

function NewChannel({ onCancel }: { onCancel: () => void }) {
  const me = useMe();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const trusted = me.data?.account.trust_tier === 'trusted' || me.data?.account.trust_tier === 'partner';
  const [name, setName] = useState('');
  const [resolution, setResolution] = useState(720);
  const [profile, setProfile] = useState<'abr_basic' | 'passthrough'>('abr_basic');
  const [record, setRecord] = useState(true);
  const [replay, setReplay] = useState(false);
  const [chat, setChat] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const defaults: Record<string, unknown> = { max_resolution: resolution, record };
      if (record && replay) defaults.replay = true;
      if (chat) defaults.chat = { enabled: true, reactions: true };
      const c = await api<CreatedChannel>('/v1/channels', { method: 'POST', json: { name: name.trim() || null, profile, defaults } });
      channelSecrets.set(c.id, { stream_key: c.stream_key, whip_url: c.whip_url });
      await qc.invalidateQueries({ queryKey: ['channels'] });
      navigate({ to: '/channels/$id', params: { id: c.id } });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create the channel.');
      setBusy(false);
    }
  };

  return (
    <form className="card" onSubmit={submit} style={{ gap: 18, maxWidth: 720 }}>
      <label className="field">
        Name
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} placeholder="Sunday service" autoFocus />
        <span className="hint">Each of its streams is named after the channel.</span>
      </label>
      <div className="grid-2">
        <label className="field">
          Highest resolution you will send
          <select className="input" value={resolution} onChange={(e) => setResolution(Number(e.target.value))}>
            <option value={480}>480p</option>
            <option value={720}>720p</option>
            <option value={1080} disabled={!trusted}>
              1080p{trusted ? '' : ' (after your account is trusted)'}
            </option>
          </select>
        </label>
        <label className="field">
          Processing
          <select className="input" value={profile} onChange={(e) => setProfile(e.target.value as typeof profile)}>
            <option value="abr_basic">Adaptive: several qualities</option>
            <option value="passthrough">Passthrough: exactly what you send</option>
          </select>
          <span className="hint">Fixed for the channel: it is part of the RTMP address.</span>
        </label>
      </div>
      <label className="check">
        <input type="checkbox" checked={record} onChange={(e) => setRecord(e.target.checked)} />
        Record every stream
      </label>
      {record && (
        <label className="check" style={{ marginTop: -6 }}>
          <input type="checkbox" checked={replay} onChange={(e) => setReplay(e.target.checked)} />
          Replay: between streams, the channel's page plays the newest one
        </label>
      )}
      <label className="check">
        <input type="checkbox" checked={chat} onChange={(e) => setChat(e.target.checked)} />
        Chat and reactions on every stream (bans carry from one stream to the next)
      </label>
      {error && <p className="error-text" role="alert" style={{ margin: 0 }}>{error}</p>}
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <button type="button" className="btn" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn primary" disabled={busy}>
          {busy ? 'Creating…' : 'Create channel'}
        </button>
      </div>
    </form>
  );
}
